/*
 * SlotEditModal — DEV-1188 Phase 2 (unified Role tray, 2026-06-01).
 *
 * Single tray for every property of a community role slot:
 *
 *   - WHO fills the role (dropdown: Platform / External / Vacant)
 *       Platform → search + pick L4E account holder
 *       External → display name + photo + contact fields
 *       Vacant   → no occupant
 *   - WHAT the role looks like (always shown):
 *       Display title (required), sub-label, catalog role, notes,
 *       hidden-from-public flag.
 *
 * Replaces the prior split between SlotAssignmentModal (WHO) and
 * SlotEditModal (WHAT). One surface, one save, one cognitive model.
 *
 * Modes:
 *   - Create mode  — called from UnifiedTeamTab's "Add Role" button with
 *                    a scaffolded blank slot. Save POSTs a new row via
 *                    upsertRoleSlot.
 *   - Edit mode    — called from the per-row pencil with the slot's
 *                    current state. Save PUTs the same row.
 *
 * Photo upload pattern selectively migrated from CommunityEnablersTab:
 *   pickFile → CircleCropModal → uploadDataUrl → store URL on slot.
 *
 * Save → upsertRoleSlot. Server-side ReconcileSlotRoleGrant cascades
 * the V2 grant when sourceDefinitionGuid changes; the existing controller
 * guard skips Rhythm task materialization when linked_user_guid is null
 * (no orphan tasks for external/vacant slots).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  upsertRoleSlot,
  deleteRoleSlot,
  type RoleSlotResponse,
} from '../../api/communityRoleSlots';
import type { EffectiveRoleDefinitionResponse } from '../../api/roleDefinitionOverrides';
import { permissionsApiV2 } from '../../security/v2/apiClient';
import type { PermissionSetDto } from '../../security/v2/types';
import { useApiRoleRhythmDetailsForDefinition } from '../../api/useApiRoleRhythmDetailsForDefinition';
import FlipTeamMemberCard from '../TeamMemberCard/FlipTeamMemberCard';
import { uploadDataUrl, fileUrl } from '../../api/fileStorage';
import { mediaSrc } from '../../api/mediaUrl';
import CircleCropModal from '../CircleCropModal/CircleCropModal';
import { Tray } from '../Tray/Tray';
import styles from './SlotAssignmentModal.module.css';
import editStyles from './SlotEditModal.module.css';

const UPLOAD_BUCKET = 'community-leaders';
const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

type FillMode = 'linked' | 'external' | 'vacant';

export interface SlotEditModalProps {
  slot: RoleSlotResponse;
  communityGuid: string;
  catalog: EffectiveRoleDefinitionResponse[];
  /** Platform user roster — needed for the linked-user picker. */
  users: Array<Record<string, any>>;
  /** Optional title override. Default: "Edit Role". Caller passes
   *  "Add Role" for the create flow. */
  titleOverride?: string;
  /** When true, the slot is being created (no existing DB row). Hides
   *  the Remove button — there's nothing to remove yet. */
  isCreate?: boolean;
  onClose: () => void;
  onSaved?: (saved: RoleSlotResponse) => void;
  /** Called after a successful delete with the removed slot's guid so
   *  the parent can drop it from local state. */
  onDeleted?: (deletedRoleSlotGuid: string) => void;
}

const SlotEditModal: React.FC<SlotEditModalProps> = ({
  slot,
  communityGuid,
  catalog,
  users,
  titleOverride,
  isCreate = false,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const { t } = useAppTranslation('common');

  // ── Fill mode (WHO) ──────────────────────────────────────────
  // DEV-1193 follow-up: also consider slot.openAssignment when computing
  // the initial mode/picker state. For a slot with a pending invitation,
  // slot.linkedUserGuid is null (the invitee hasn't accepted yet) but
  // openAssignment.userGuid carries the invited user. Without this the
  // tray opens as Vacant with no profile — confusing because the admin
  // sees "Pending: RISSAR" on the card but an empty picker in the tray.
  const initialMode: FillMode = (slot.linkedUserGuid || slot.openAssignment?.userGuid)
    ? 'linked'
    : slot.manualName
    ? 'external'
    : 'vacant';
  const [fillMode, setFillMode] = useState<FillMode>(initialMode);

  // Linked-user picker. Search input doubles as the display surface for
  // the current selection — clicking a result populates the input with
  // the picked user's display name; typing again clears the selection
  // so search results refresh against the new query.
  const [linkedUserGuid, setLinkedUserGuid] = useState<string>(
    slot.linkedUserGuid || slot.openAssignment?.userGuid || ''
  );
  const [userSearch, setUserSearch] = useState<string>('');

  // External member fields
  const [manualName, setManualName] = useState<string>(slot.manualName || '');
  const [manualPhotoUrl, setManualPhotoUrl] = useState<string>(slot.manualPhotoUrl || '');
  const [manualEmail, setManualEmail] = useState<string>(slot.manualEmail || '');
  const [manualPhone, setManualPhone] = useState<string>(slot.manualPhone || '');
  const [externalProfileUrl, setExternalProfileUrl] = useState<string>(slot.externalProfileUrl || '');

  // Role config (always shown)
  const [displayTitle, setDisplayTitle] = useState<string>(slot.displayTitle);
  const [manualTitle, setManualTitle] = useState<string>(slot.manualTitle || '');
  const [sourceDefinitionGuid, setSourceDefinitionGuid] = useState<string>(
    slot.sourceDefinitionGuid || ''
  );
  const [notes, setNotes] = useState<string>(slot.notes || '');
  const [isHidden, setIsHidden] = useState<boolean>(slot.isHidden);

  // DEV-1159 Phase A — role-definition fields (denormalized per slot per
  // the locked design 2026-06-01). Same per-slot storage pattern the
  // legacy ProjectTeam RoleSlotEditor uses.
  const [description, setDescription] = useState<string>(slot.description || '');
  const [skillsRequired, setSkillsRequired] = useState<string[]>(
    Array.isArray(slot.skillsRequired) ? slot.skillsRequired : []
  );
  const [skillDraft, setSkillDraft] = useState<string>('');
  const [timeCommitment, setTimeCommitment] = useState<string>(slot.timeCommitment || '');
  const [permissionSetGuid, setPermissionSetGuid] = useState<string>(
    slot.permissionSetGuid || ''
  );
  const [bundles, setBundles] = useState<PermissionSetDto[]>([]);

  // DEV-1159 Sub-phase B1.5 — tab state. Three tabs organize the
  // increasingly-rich form: Person (who fills), Role (slot + role-def
  // fields), Rhythm (read-only display in B1; editing in B2/B3).
  // Hidden flag + Notes stay above the footer as a small admin-only
  // strip, always visible regardless of active tab.
  type ActiveTab = 'person' | 'role' | 'rhythm';
  const [activeTab, setActiveTab] = useState<ActiveTab>('person');

  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Photo upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUrlInput, setShowUrlInput] = useState<boolean>(false);
  const [urlDraft, setUrlDraft] = useState<string>('');
  const [pendingCrop, setPendingCrop] = useState<
    | null
    | { src: string; w: number; h: number }
  >(null);

  useEffect(() => {
    // DEV-1193 follow-up: openAssignment carries the pending invitee
    // (and active occupant) — used to populate the picker so the tray
    // doesn't open blank when admin clicks an in-flight invitation.
    const nextMode: FillMode = (slot.linkedUserGuid || slot.openAssignment?.userGuid)
      ? 'linked'
      : slot.manualName
      ? 'external'
      : 'vacant';
    setFillMode(nextMode);
    setLinkedUserGuid(slot.linkedUserGuid || slot.openAssignment?.userGuid || '');
    setUserSearch('');
    setManualName(slot.manualName || '');
    setManualPhotoUrl(slot.manualPhotoUrl || '');
    setManualEmail(slot.manualEmail || '');
    setManualPhone(slot.manualPhone || '');
    setExternalProfileUrl(slot.externalProfileUrl || '');
    setDisplayTitle(slot.displayTitle);
    setManualTitle(slot.manualTitle || '');
    setSourceDefinitionGuid(slot.sourceDefinitionGuid || '');
    setNotes(slot.notes || '');
    setIsHidden(slot.isHidden);
    // DEV-1159 Phase A
    setDescription(slot.description || '');
    setSkillsRequired(Array.isArray(slot.skillsRequired) ? slot.skillsRequired : []);
    setSkillDraft('');
    setTimeCommitment(slot.timeCommitment || '');
    setPermissionSetGuid(slot.permissionSetGuid || '');
    setError(null);
  }, [
    slot.roleSlotGuid,
    slot.linkedUserGuid,
    // DEV-1468: the mode derivation above READS openAssignment, so it must be a
    // dependency. It wasn't (pre-existing exhaustive-deps error), which means
    // after the open assignment is revoked the effect would not re-run and the
    // tray would keep showing the stale 'linked' / "Pending invited user" mode
    // against fresh data. Exactly the class of latent bug CLAUDE.md's deps rule
    // exists to catch.
    slot.openAssignment?.userGuid,
    slot.openAssignment?.status,
    slot.manualName,
    slot.manualPhotoUrl,
    slot.manualEmail,
    slot.manualPhone,
    slot.externalProfileUrl,
    slot.displayTitle,
    slot.manualTitle,
    slot.sourceDefinitionGuid,
    slot.notes,
    slot.isHidden,
    slot.description,
    slot.skillsRequired,
    slot.timeCommitment,
    slot.permissionSetGuid,
  ]);

  // DEV-1159 Sub-phase B1 — fetch the rhythm details for the currently
  // selected catalog role. When the dropdown changes, the hook refetches.
  // Returns null when the definition has no rhythm attached — section
  // renders nothing in that case.
  const {
    details: rhythmDetails,
    loading: rhythmLoading,
    error: rhythmError,
  } = useApiRoleRhythmDetailsForDefinition(sourceDefinitionGuid || null);

  // DEV-1159 Phase A — fetch the community-scope permission bundles for
  // the dropdown. Cached for the lifetime of the tray.
  useEffect(() => {
    let cancelled = false;
    permissionsApiV2
      .listPermissionSetsForScope('community', communityGuid)
      .then((rows) => {
        if (!cancelled) setBundles(rows);
      })
      .catch((err) => {
        // Soft fail — the dropdown just shows empty, admin can save without
        // a bundle. The slot retains whatever permission_set_guid was last set.
        console.warn('[SlotEditModal] permission bundle fetch failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [communityGuid]);

  // ── User search (search-driven, no list-all) ─────────────────
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter((u) =>
        [u.display_name, u.name, u.email, u.screen_name]
          .filter(Boolean)
          .some((s: string) => String(s).toLowerCase().includes(q))
      )
      .slice()
      .sort((a, b) =>
        String(a.display_name || a.name || a.email || '').localeCompare(
          String(b.display_name || b.name || b.email || ''),
          undefined,
          { numeric: true, sensitivity: 'base' }
        )
      )
      .slice(0, 50);
  }, [users, userSearch]);

  // ── Avatar handlers ──────────────────────────────────────────

  const handleAvatarFile = useCallback((file: File) => {
    if (file.size > MAX_RAW_FILE_BYTES) {
      alert(`Image too large (max ${MAX_RAW_FILE_BYTES / 1024 / 1024}MB).`);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setPendingCrop({ src: url, w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      alert('Failed to load image.');
    };
    img.src = url;
  }, []);

  const handleAvatarCropConfirm = useCallback(
    async (dataUrl: string) => {
      if (!pendingCrop) return;
      URL.revokeObjectURL(pendingCrop.src);
      setPendingCrop(null);
      const ts = Date.now();
      try {
        const result = await uploadDataUrl(
          dataUrl,
          UPLOAD_BUCKET,
          slot.roleSlotGuid,
          `avatar-${ts}.png`,
        );
        setManualPhotoUrl(`${fileUrl(result.key)}?v=${ts}`);
      } catch (err) {
        console.warn('[SlotEditModal] Avatar upload failed; storing data URL.', err);
        setManualPhotoUrl(dataUrl);
      }
    },
    [pendingCrop, slot.roleSlotGuid],
  );

  const handleAvatarCropCancel = useCallback(() => {
    if (pendingCrop) URL.revokeObjectURL(pendingCrop.src);
    setPendingCrop(null);
  }, [pendingCrop]);

  // ── Remove (destructive) ─────────────────────────────────────
  // Deletes the slot entirely. If a user was assigned, the slot-anchored
  // V2 grant is revoked server-side (TU-5 ReconcileSlotRoleGrant
  // handles the cascade in the controller's Delete path). Confirm
  // dialog names the consequence so the admin isn't surprised.
  const handleRemove = async () => {
    if (busy) return;
    // Build a consequence string for the confirm. Names the current
    // occupant if there is one — makes the cascade explicit.
    const occupantLabel = (() => {
      if (fillMode === 'linked' && linkedUserGuid) {
        const u = users.find(
          (x) => String(x.user_guid || '').toUpperCase() === linkedUserGuid.toUpperCase(),
        );
        return String(u?.display_name || u?.name || u?.email || '').trim();
      }
      if (fillMode === 'external' && manualName.trim()) return manualName.trim();
      return '';
    })();
    const roleLabel = displayTitle.trim() || slot.displayTitle || 'this role';
    const confirmMessage = occupantLabel
      ? t('unifiedTeamTab.editRole.removeConfirmWithOccupant', {
          defaultValue: 'Remove the "{{role}}" role? {{occupant}} will lose this role and any associated permissions. This cannot be undone.',
          role: roleLabel,
          occupant: occupantLabel,
        })
      : t('unifiedTeamTab.editRole.removeConfirmEmpty', {
          defaultValue: 'Remove the "{{role}}" role? This cannot be undone.',
          role: roleLabel,
        });
    if (!confirm(confirmMessage as string)) return;

    setBusy(true);
    setError(null);
    try {
      await deleteRoleSlot(slot.roleSlotGuid);
      // Refresh the Alignment-tab CommunityEnablers grid.
      window.dispatchEvent(
        new CustomEvent('l4e_community_enablers_persisted', {
          detail: { communityGuid },
        }),
      );
      onDeleted?.(slot.roleSlotGuid);
      onClose();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  // ── DEV-1193 Phase 5: Revoke ────────────────────────────────
  // Distinct from Remove (above): Revoke ends the assignment cleanly
  // while leaving the slot intact. Backend endpoint handles both
  // pending invitations (status='pending_invitation') and active
  // assignments (status='active'). Confirm copy adapts to the case.
  const handleRevoke = async () => {
    if (busy) return;
    if (!slot.openAssignment) return;
    const inviteeName = slot.openAssignment.userDisplayName || 'the invitee';
    const roleLabel = displayTitle.trim() || slot.displayTitle || 'this role';
    const confirmMessage = slot.openAssignment.status === 'pending_invitation'
      ? t('unifiedTeamTab.editRole.revokeConfirmPending', {
          defaultValue: 'Withdraw the invitation to "{{role}}" for {{invitee}}? The slot stays in place; you can invite someone else.',
          role: roleLabel,
          invitee: inviteeName,
        })
      : t('unifiedTeamTab.editRole.revokeConfirmActive', {
          defaultValue: 'End {{invitee}}\'s assignment to "{{role}}"? They will be notified; rhythm tasks fall back to the community Lead; the slot stays available for re-invitation.',
          role: roleLabel,
          invitee: inviteeName,
        });
    if (!confirm(confirmMessage as string)) return;

    setBusy(true);
    setError(null);
    try {
      const { revokeCommunityRoleSlotAssignment } = await import(
        '../../api/communityRoleSlotInvitations'
      );
      await revokeCommunityRoleSlotAssignment(slot.openAssignment.assignmentGuid, {
        reason: null,
      });
      window.dispatchEvent(
        new CustomEvent('l4e_community_enablers_persisted', {
          detail: { communityGuid },
        }),
      );
      onClose();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  // ── Save ─────────────────────────────────────────────────────

  const handleSave = async () => {
    if (busy) return;
    const title = displayTitle.trim();
    if (!title) {
      setError(t('unifiedTeamTab.editRole.titleRequired', { defaultValue: 'Display title is required.' }) as string);
      return;
    }
    if (fillMode === 'linked' && !linkedUserGuid) {
      setError(t('unifiedTeamTab.editRole.userRequired', { defaultValue: 'Select a platform user.' }) as string);
      return;
    }
    if (fillMode === 'external' && !manualName.trim()) {
      setError(t('unifiedTeamTab.editRole.nameRequired', { defaultValue: 'Display name is required for external members.' }) as string);
      return;
    }

    setBusy(true);
    setError(null);

    // ── Denormalization for linked mode ──
    // The Alignment tab's CommunityEnablers component reads name + photo
    // from manual_name / manual_photo_url unconditionally (legacy
    // pattern: denormalize at save time, not resolve at render time).
    // For linked-user mode, copy the picked user's display name, photo,
    // and email onto the slot so the public card has something to
    // render. Matches the legacy CommunityEnablersTab behavior. The
    // tradeoff is that profile updates by the user don't propagate to
    // the slot card until someone re-saves the slot — accepted as the
    // existing platform behavior.
    let linkedManualName: string | null = null;
    let linkedManualPhotoUrl: string | null = null;
    let linkedManualEmail: string | null = null;
    if (fillMode === 'linked' && linkedUserGuid) {
      const u = users.find(
        (x) => String(x.user_guid || '').toUpperCase() === linkedUserGuid.toUpperCase(),
      );
      if (u) {
        linkedManualName = String(u.display_name || u.name || u.email || '').trim() || null;
        linkedManualPhotoUrl = String(u.profile_image_url || '').trim() || null;
        linkedManualEmail = String(u.email || '').trim() || null;
      }
    }

    // ── DEV-1193 Phase 3c: invitation fork ─────────────────────────
    // When fillMode='linked' and the picked user differs from the slot's
    // current occupant, the save splits into two server calls:
    //   1. Upsert the slot's metadata WITHOUT touching linked_user_guid
    //      (preserve current occupancy; the invitation flow owns the
    //      transition).
    //   2. POST /api/community-role-slot-invitations to create a
    //      pending invitation. Invitee gets a bell + Action Center row
    //      and the server flips linked_user_guid on accept.
    // External and vacant modes keep the direct-write path — there's
    // no platform user to invite.
    const currentOccupantUpper = (slot.linkedUserGuid || '').toUpperCase();
    const pickedUserUpper = (linkedUserGuid || '').toUpperCase();
    const triggerInvitation =
      fillMode === 'linked' &&
      !!linkedUserGuid &&
      currentOccupantUpper !== pickedUserUpper;

    // ── DEV-1468: switching a slot to External or Vacant must REVOKE any open
    //    assignment on it (Skip ruling 2026-07-13: revoke pending AND active).
    //
    //    THE BUG THIS FIXES. The save wrote the external fields correctly
    //    (manual_name set, linked_user_guid nulled) but left the slot's open
    //    assignment row alive. On reload the mode derivation runs
    //      (slot.linkedUserGuid || slot.openAssignment?.userGuid) ? 'linked' : …
    //    so the stale pending invitation WON, the tray flipped back to
    //    "internal user / Pending invited user", and the external member the
    //    admin had just saved was invisible — still in the DB, merely masked.
    //    Same root cause family as DEV-1428/DEV-1429: an assignment that
    //    outlives the intent that created it.
    //
    //    Declaring "this role is held by an external person" (or by nobody) IS
    //    the removal of whoever was in it. Revoking first also keeps the
    //    one-open-assignment-per-slot invariant from 409-ing a later re-invite.
    const openAssignment = slot.openAssignment;
    const mustRevokeOpen =
      (fillMode === 'external' || fillMode === 'vacant') &&
      !!openAssignment?.assignmentGuid &&
      (openAssignment.status === 'pending_invitation' || openAssignment.status === 'active');

    try {
      if (mustRevokeOpen && openAssignment) {
        const { revokeCommunityRoleSlotAssignment } = await import(
          '../../api/communityRoleSlotInvitations'
        );
        await revokeCommunityRoleSlotAssignment(openAssignment.assignmentGuid, {
          reason:
            fillMode === 'external'
              ? 'Role reassigned to an external member.'
              : 'Role vacated.',
        });
      }

      const saved = await upsertRoleSlot(slot.roleSlotGuid, communityGuid, {
        roleSlotGuid: slot.roleSlotGuid,
        displayTitle: title,
        sortOrder: slot.sortOrder,
        isHidden,
        // WHO fields — driven by mode. DEV-1193 Phase 3c: when an
        // invitation will fire below, preserve the existing
        // linked_user_guid + manual_* cache so the slot stays at its
        // current occupancy until the invitee accepts. Otherwise (same
        // user or external/vacant modes) write directly as before.
        linkedUserGuid: triggerInvitation
          ? slot.linkedUserGuid
          : (fillMode === 'linked' ? linkedUserGuid : null),
        manualName: triggerInvitation
          ? slot.manualName
          : (fillMode === 'external' ? manualName.trim() :
             fillMode === 'linked' ? linkedManualName :
             null),
        manualPhotoUrl: triggerInvitation
          ? slot.manualPhotoUrl
          : (fillMode === 'external' ? (manualPhotoUrl.trim() || null) :
             fillMode === 'linked' ? linkedManualPhotoUrl :
             null),
        manualEmail: triggerInvitation
          ? slot.manualEmail
          : (fillMode === 'external' ? (manualEmail.trim() || null) :
             fillMode === 'linked' ? linkedManualEmail :
             null),
        manualPhone: fillMode === 'external' ? (manualPhone.trim() || null) : null,
        externalProfileUrl: fillMode === 'external' ? (externalProfileUrl.trim() || null) : null,
        // WHAT fields — always saved
        manualTitle: manualTitle.trim() || null,
        notes: notes.trim() || null,
        // External members never carry a Tier-1 catalog link (no rhythm,
        // no permission cascade applies). Defense in depth: even if
        // stale state has a value, nullify it on save when fillMode is
        // 'external'.
        sourceDefinitionGuid: fillMode === 'external' ? null : (sourceDefinitionGuid || null),
        // DEV-1159 Phase A — ROLE DEFINITION fields (denormalized per slot).
        description: description.trim() || null,
        skillsRequired,
        timeCommitment: timeCommitment.trim() || null,
        // Empty string = clear (sent as '00000000-…' so the server's
        // Guid.Empty branch fires); non-empty sets; bundle dropdown
        // always emits an explicit value because user touches it.
        permissionSetGuid: permissionSetGuid || '00000000-0000-0000-0000-000000000000',
      });

      // ── DEV-1193 Phase 3c: create the invitation as step 2 ──
      if (triggerInvitation && linkedUserGuid) {
        const { createCommunityRoleSlotInvitation } = await import(
          '../../api/communityRoleSlotInvitations'
        );
        await createCommunityRoleSlotInvitation(saved.roleSlotGuid, {
          userGuid: linkedUserGuid,
          invitationMessage: null,
        });
      }
      // Notify the public-facing Alignment-tab CommunityEnablers
      // component to refresh from the server. Without this, the leader
      // grid on Alignment stays stale until the user reloads the page.
      // The event matches the contract in
      // src/Data/CommunityConfig/communityEnablers.ts notifyPersisted().
      window.dispatchEvent(
        new CustomEvent('l4e_community_enablers_persisted', {
          detail: { communityGuid },
        }),
      );
      onSaved?.(saved);
      onClose();
    } catch (e: any) {
      // DEV-1428 — the invitation endpoint enforces one open assignment per
      // slot (409). Clearing the picker pill removes the invitee from the
      // picker's local state but does NOT revoke the underlying pending
      // invitation, so a stale invite silently blocked re-inviting. When that
      // happens, name the blocker and point to the Revoke control below rather
      // than showing the raw "already has an open assignment" 409.
      if (e?.status === 409 && slot.openAssignment) {
        setError(
          t('unifiedTeamTab.editRole.pendingBlocksInviteError', {
            defaultValue:
              'This slot still has a pending invitation to {{name}}. Revoke it below, then send the invitation.',
            name: slot.openAssignment.userDisplayName || 'someone',
          }) as string,
        );
      } else {
        setError(String(e?.message || e));
      }
    } finally {
      setBusy(false);
    }
  };

  const sortedCatalog = catalog
    .slice()
    .sort((a, b) => a.label.localeCompare(b.label));

  const initial = (manualName || displayTitle || '?').trim().charAt(0).toUpperCase();

  return (
    <>
      <Tray
        open={!pendingCrop /* hide while crop is in flight */}
        onClose={onClose}
        title={titleOverride ?? (t('unifiedTeamTab.editRole.title', { defaultValue: 'Edit Role' }) as string)}
        scrimClickDisabled={busy}
      >
        <Tray.Body className={styles.body}>
          {/* ── Tab strip ───────────────────────────────────────── */}
          <nav className={editStyles.tabStrip} role="tablist" aria-label={t('unifiedTeamTab.editRole.tabsAriaLabel', { defaultValue: 'Role editor sections' }) as string}>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'person'}
              className={activeTab === 'person' ? editStyles.tabActive : editStyles.tab}
              onClick={() => setActiveTab('person')}
              disabled={busy}
            >
              {t('unifiedTeamTab.editRole.tabPerson', { defaultValue: 'Person' })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'role'}
              className={activeTab === 'role' ? editStyles.tabActive : editStyles.tab}
              onClick={() => setActiveTab('role')}
              disabled={busy}
            >
              {t('unifiedTeamTab.editRole.tabRole', { defaultValue: 'Role' })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'rhythm'}
              className={activeTab === 'rhythm' ? editStyles.tabActive : editStyles.tab}
              onClick={() => setActiveTab('rhythm')}
              disabled={busy}
            >
              {t('unifiedTeamTab.editRole.tabRhythm', { defaultValue: 'Rhythm' })}
            </button>
          </nav>

          {/* ── PERSON tab ─────────────────────────────────────── */}
          {activeTab === 'person' && (<>
          {/* ── Who fills this role? (dropdown) ─────────────────── */}
          <div className={styles.modePane}>
            <label className={styles.fieldLabel}>
              {t('unifiedTeamTab.editRole.fillModeLabel', { defaultValue: 'Who fills this role?' })}
            </label>
            <select
              className={styles.textInput}
              value={fillMode}
              onChange={(e) => setFillMode(e.target.value as FillMode)}
              disabled={busy}
            >
              <option value="linked">
                {t('unifiedTeamTab.editRole.fillModeLinked', { defaultValue: 'Platform member (account holder)' })}
              </option>
              <option value="external">
                {t('unifiedTeamTab.editRole.fillModeExternal', { defaultValue: 'External member (figurehead — no L4E account)' })}
              </option>
              <option value="vacant">
                {t('unifiedTeamTab.editRole.fillModeVacant', { defaultValue: 'Vacant (no occupant yet)' })}
              </option>
            </select>
          </div>

          {/* ── Linked-user picker (pill-style, matches Primary Location) ──
              Single-line input wrap: when a user is selected, a pill chip
              shows their name with × to clear; an inline input on the
              same line accepts "Type to change…" search input. Dropdown
              of results appears below. Mirrors GeoLocationPicker shape. */}
          {fillMode === 'linked' && (() => {
            const selectedUser = linkedUserGuid
              ? users.find(
                  (u) => String(u.user_guid || '').toUpperCase() === linkedUserGuid.toUpperCase(),
                )
              : null;
            const selectedName = selectedUser
              ? String(selectedUser.display_name || selectedUser.name || selectedUser.email || linkedUserGuid)
              : linkedUserGuid;
            return (
              <div className={styles.modePane}>
                <div className={editStyles.pickerWrap}>
                  {linkedUserGuid && (
                    <span className={editStyles.pickerPill} title={selectedName}>
                      <span className={editStyles.pickerPillLabel}>{selectedName}</span>
                      <button
                        type="button"
                        className={editStyles.pickerPillClear}
                        onClick={() => {
                          setLinkedUserGuid('');
                          setUserSearch('');
                        }}
                        disabled={busy}
                        aria-label={t('unifiedTeamTab.assignModal.clearSelection', { defaultValue: 'Clear selection' }) as string}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  <input
                    type="search"
                    className={editStyles.pickerInput}
                    placeholder={
                      linkedUserGuid
                        ? (t('unifiedTeamTab.assignModal.typeToChange', { defaultValue: 'Type to change…' }) as string)
                        : (t('unifiedTeamTab.assignModal.searchPlaceholder', { defaultValue: 'Search by name or email…' }) as string)
                    }
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      // Typing on a row with a pill clears the prior
                      // selection so search filters against the new query.
                      if (linkedUserGuid && e.target.value.trim().length > 0) {
                        setLinkedUserGuid('');
                      }
                    }}
                    disabled={busy}
                  />
                </div>

                {/* Person preview — when a user is linked AND no active
                    search, show their FlipTeamMemberCard. Front face is
                    the leader portrait; hover/tap flips to reveal the
                    impact signature. Gives admins a quick visual + impact
                    read on who they assigned without leaving the tray. */}
                {linkedUserGuid && !userSearch.trim() && selectedUser && (
                  <div className={editStyles.personPreview}>
                    <FlipTeamMemberCard
                      displayName={String(
                        selectedUser.display_name || selectedUser.name || selectedUser.email || ''
                      )}
                      photoUrl={selectedUser.profile_image_url}
                      roleTitle={displayTitle || t('unifiedTeamTab.editRole.tabRole', { defaultValue: 'Role' }) as string}
                      linkedUserGuid={linkedUserGuid}
                      userGuid={linkedUserGuid}
                      contactEmail={selectedUser.email}
                      avatarVariant="l4e"
                      splitName
                      size="md"
                    />
                  </div>
                )}

                {/* Results dropdown — only shows when typing produces hits
                    or when nothing is selected yet (start-typing hint).
                    If a user is already picked and they haven't typed,
                    suppress the list to keep the surface compact. */}
                {(userSearch.trim() || !linkedUserGuid) && (
                  <ul className={styles.userList}>
                    {filteredUsers.length === 0 ? (
                      <li className={styles.emptyHint}>
                        {userSearch.trim()
                          ? t('unifiedTeamTab.assignModal.noResults', { defaultValue: 'No users found.' })
                          : t('unifiedTeamTab.assignModal.startTyping', { defaultValue: 'Start typing to search platform users…' })}
                      </li>
                    ) : (
                      filteredUsers.map((u) => {
                        const guid = String(u.user_guid || '');
                        const isPicked = guid.toUpperCase() === linkedUserGuid.toUpperCase();
                        const display = u.display_name || u.name || u.email || guid;
                        return (
                          <li
                            key={guid}
                            className={isPicked ? styles.userRowActive : styles.userRow}
                            // mousedown not click so it fires before any
                            // input blur handler that would close the
                            // dropdown.
                            onMouseDown={() => {
                              if (busy) return;
                              setLinkedUserGuid(guid);
                              // Clear the search now that we've made a
                              // selection — the pill carries the identity;
                              // the input goes back to "Type to change…".
                              setUserSearch('');
                            }}
                          >
                            <div className={styles.userName}>{display}</div>
                            {(u.email || u.name) && (
                              <div className={styles.userMeta}>{u.email || ''}</div>
                            )}
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </div>
            );
          })()}

          {/* ── External member fields ─────────────────────────── */}
          {fillMode === 'external' && (
            <>
              <div className={editStyles.photoRow}>
                <div className={editStyles.photoAvatar}>
                  {manualPhotoUrl ? (
                    <img src={mediaSrc(manualPhotoUrl) ?? undefined} alt="" />
                  ) : (
                    <span className={editStyles.photoInitial}>{initial}</span>
                  )}
                </div>
                <div className={editStyles.photoControls}>
                  <label className={styles.fieldLabel}>
                    {t('unifiedTeamTab.editRole.photoLabel', { defaultValue: 'Photo' })}
                  </label>
                  <div className={editStyles.photoBtnRow}>
                    <button
                      type="button"
                      className={editStyles.photoBtn}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={busy}
                    >
                      {manualPhotoUrl
                        ? t('unifiedTeamTab.editRole.changePhoto', { defaultValue: 'Change photo' })
                        : t('unifiedTeamTab.editRole.uploadPhoto', { defaultValue: 'Upload photo' })}
                    </button>
                    <button
                      type="button"
                      className={editStyles.photoBtn}
                      onClick={() => {
                        setShowUrlInput((v) => !v);
                        setUrlDraft(manualPhotoUrl);
                      }}
                      disabled={busy}
                    >
                      {t('unifiedTeamTab.editRole.pasteUrl', { defaultValue: 'Paste URL' })}
                    </button>
                    {manualPhotoUrl && (
                      <button
                        type="button"
                        className={editStyles.photoBtnDanger}
                        onClick={() => setManualPhotoUrl('')}
                        disabled={busy}
                      >
                        {t('common.remove', { defaultValue: 'Remove' })}
                      </button>
                    )}
                  </div>
                  {showUrlInput && (
                    <div className={editStyles.urlInputRow}>
                      <input
                        type="url"
                        className={styles.textInput}
                        value={urlDraft}
                        onChange={(e) => setUrlDraft(e.target.value)}
                        placeholder="https://example.com/photo.jpg"
                        autoFocus
                        disabled={busy}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setManualPhotoUrl(urlDraft.trim());
                            setShowUrlInput(false);
                          } else if (e.key === 'Escape') {
                            setShowUrlInput(false);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className={editStyles.photoBtn}
                        onClick={() => {
                          setManualPhotoUrl(urlDraft.trim());
                          setShowUrlInput(false);
                        }}
                        disabled={busy}
                      >
                        {t('common.save', { defaultValue: 'Save' })}
                      </button>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleAvatarFile(f);
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>

              <div className={styles.modePane}>
                <label className={styles.fieldLabel}>
                  {t('unifiedTeamTab.editRole.displayNameLabel', { defaultValue: 'Display name' })}
                </label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder={t('unifiedTeamTab.editRole.displayNamePlaceholder', { defaultValue: 'e.g. John Smith' }) as string}
                  disabled={busy}
                />
              </div>

              <div className={styles.modePane}>
                <label className={styles.fieldLabel}>
                  {t('unifiedTeamTab.editRole.emailLabel', { defaultValue: 'Email' })}
                </label>
                <input
                  type="email"
                  className={styles.textInput}
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  placeholder="email@example.com"
                  disabled={busy}
                />
              </div>

              <div className={styles.modePane}>
                <label className={styles.fieldLabel}>
                  {t('unifiedTeamTab.editRole.phoneLabel', { defaultValue: 'Phone' })}
                </label>
                <input
                  type="tel"
                  className={styles.textInput}
                  value={manualPhone}
                  onChange={(e) => setManualPhone(e.target.value)}
                  placeholder="+1 555 555 5555"
                  disabled={busy}
                />
              </div>

              <div className={styles.modePane}>
                <label className={styles.fieldLabel}>
                  {t('unifiedTeamTab.editRole.externalUrlLabel', { defaultValue: 'External profile URL' })}
                </label>
                <input
                  type="url"
                  className={styles.textInput}
                  value={externalProfileUrl}
                  onChange={(e) => setExternalProfileUrl(e.target.value)}
                  placeholder="https://example.com/profile"
                  disabled={busy}
                />
              </div>
            </>
          )}

          {fillMode === 'vacant' && (
            <div className={styles.modePane}>
              <div className={styles.clearNotice}>
                {t('unifiedTeamTab.editRole.vacantNotice', {
                  defaultValue: 'This slot will be marked Vacant. The role exists on the community leaderboard but has no occupant.',
                })}
              </div>
            </div>
          )}

          {/* Hide-from-public flag — about whether THIS slot's occupant
              (or vacancy) appears on the community leaderboard. Belongs
              on Person because it's a visibility decision about the
              human side of the slot, not about the role definition. */}
          <div className={styles.modePane}>
            <label className={editStyles.checkboxRow}>
              <input
                type="checkbox"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                disabled={busy}
              />
              <span>
                <span className={editStyles.checkboxLabel}>
                  {t('unifiedTeamTab.editRole.hiddenLabel', {
                    defaultValue: 'Hide from public team display',
                  })}
                </span>
                <div className={styles.helpText}>
                  {t('unifiedTeamTab.editRole.hiddenHelp', {
                    defaultValue:
                      'Use for privacy preferences or transition periods. Admins still see this slot in Management.',
                  })}
                </div>
              </span>
            </label>
          </div>

          {/* ── DEV-1193 Phase 5: Revoke button ────────────────────
              Surfaces when the slot has an open invitation (pending or
              active assignment). Confirm-gated. Calls the backend
              revoke endpoint which ends the assignment cleanly without
              destroying the slot itself (distinct from the Role-tab
              Remove button, which destroys the whole slot + cascades). */}
          {slot.openAssignment && (
            <div className={styles.modePane}>
              <button
                type="button"
                className={editStyles.photoBtnDanger}
                onClick={handleRevoke}
                disabled={busy}
              >
                {slot.openAssignment.status === 'pending_invitation'
                  ? t('unifiedTeamTab.editRole.revokePendingBtn', {
                      defaultValue: 'Revoke invitation',
                    })
                  : t('unifiedTeamTab.editRole.revokeActiveBtn', {
                      defaultValue: 'Revoke assignment',
                    })}
              </button>
              <div className={styles.helpText}>
                {slot.openAssignment.status === 'pending_invitation'
                  ? t('unifiedTeamTab.editRole.revokePendingHelp', {
                      defaultValue:
                        'Withdraw the invitation. The invitee is notified; the slot stays vacant.',
                    })
                  : t('unifiedTeamTab.editRole.revokeActiveHelp', {
                      defaultValue:
                        'End this assignment. The user is notified; rhythm tasks fall back to the community Lead; the slot stays available for re-invitation.',
                    })}
              </div>
            </div>
          )}
          </>)}

          {/* ── ROLE tab ───────────────────────────────────────── */}
          {activeTab === 'role' && (<>
          {/* Group 1: Display Title + Sub-label (one identity unit). */}
          <div className={styles.modePane}>
            <label className={styles.fieldLabel}>
              {t('unifiedTeamTab.editRole.displayTitleLabel', { defaultValue: 'Display title' })}
            </label>
            <input
              type="text"
              className={styles.textInput}
              value={displayTitle}
              onChange={(e) => setDisplayTitle(e.target.value)}
              disabled={busy}
            />
            <div className={styles.helpText}>
              {t('unifiedTeamTab.editRole.displayTitleHelp', {
                defaultValue: 'The label shown on this row. Can differ from the catalog role for per-instance customization.',
              })}
            </div>

            <label className={styles.fieldLabel} style={{ marginTop: 10 }}>
              {t('unifiedTeamTab.editRole.manualTitleLabel', { defaultValue: 'Sub-label (optional)' })}
            </label>
            <input
              type="text"
              className={styles.textInput}
              value={manualTitle}
              onChange={(e) => setManualTitle(e.target.value)}
              placeholder={t('unifiedTeamTab.editRole.manualTitlePlaceholder', { defaultValue: 'e.g. U.S. House Rep. (MA-07)' }) as string}
              disabled={busy}
            />
          </div>

          {/* Group 2: Role definition + Permission bundle (catalog link
              drives the permission cascade — coupled). HIDDEN for
              external members; their slot is figurehead-only. */}
          {fillMode !== 'external' && (
            <div className={styles.modePane}>
              <label className={styles.fieldLabel}>
                {t('unifiedTeamTab.editRole.roleDefinitionLabel', { defaultValue: 'Role definition (Tier-1 catalog)' })}
              </label>
              <select
                className={styles.textInput}
                value={sourceDefinitionGuid}
                onChange={(e) => setSourceDefinitionGuid(e.target.value)}
                disabled={busy}
              >
                <option value="">
                  {t('unifiedTeamTab.editRole.noDefinition', { defaultValue: '(none — descriptive slot only)' })}
                </option>
                {sortedCatalog.map((d) => (
                  <option key={d.definitionGuid} value={d.definitionGuid}>
                    {d.label}
                    {d.roleRhythmGuid ? ' (has rhythm)' : ''}
                    {d.hasOverride ? ' (override)' : ''}
                  </option>
                ))}
              </select>
              <div className={styles.helpText}>
                {t('unifiedTeamTab.editRole.roleDefinitionHelp', {
                  defaultValue: 'Link this slot to a Tier-1 catalog role to inherit the role rhythm and permission cascade.',
                })}
              </div>

              <label className={styles.fieldLabel} style={{ marginTop: 10 }}>
                {t('unifiedTeamTab.editRole.permissionSetLabel', { defaultValue: 'Permission bundle' })}
              </label>
              <select
                className={styles.textInput}
                value={permissionSetGuid}
                onChange={(e) => setPermissionSetGuid(e.target.value)}
                disabled={busy}
              >
                <option value="">
                  {t('unifiedTeamTab.editRole.noPermissionSet', { defaultValue: '(none — descriptive slot only)' })}
                </option>
                {bundles
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((b) => (
                    <option key={b.permissionSetGuid} value={b.permissionSetGuid}>
                      {b.name}
                    </option>
                  ))}
              </select>
              <div className={styles.helpText}>
                {t('unifiedTeamTab.editRole.permissionSetHelp', {
                  defaultValue: 'The capabilities granted to whoever fills this slot (V2 cascade). Leave unset for figurehead/display-only slots.',
                })}
              </div>
            </div>
          )}

          {/* ── DEV-1159 Phase A: ROLE DEFINITION fields ────── */}
          <div className={styles.modePane}>
            <label className={styles.fieldLabel}>
              {t('unifiedTeamTab.editRole.descriptionLabel', { defaultValue: 'Description' })}
            </label>
            <textarea
              className={editStyles.textArea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('unifiedTeamTab.editRole.descriptionPlaceholder', { defaultValue: "What this role's responsible for…" }) as string}
              rows={3}
              disabled={busy}
            />
          </div>

          <div className={styles.modePane}>
            <label className={styles.fieldLabel}>
              {t('unifiedTeamTab.editRole.skillsLabel', { defaultValue: 'Skills required' })}
            </label>
            <div className={editStyles.skillChipRow}>
              {skillsRequired.map((s, idx) => (
                <span key={`${s}-${idx}`} className={editStyles.skillChip}>
                  {s}
                  <button
                    type="button"
                    className={editStyles.skillChipRemove}
                    onClick={() =>
                      setSkillsRequired((arr) => arr.filter((_, i) => i !== idx))
                    }
                    disabled={busy}
                    aria-label={t('common.remove', { defaultValue: 'Remove' }) as string}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              className={styles.textInput}
              value={skillDraft}
              onChange={(e) => setSkillDraft(e.target.value)}
              placeholder={t('unifiedTeamTab.editRole.skillsPlaceholder', { defaultValue: 'Type a skill and press Enter…' }) as string}
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  const v = skillDraft.trim();
                  if (v && !skillsRequired.includes(v)) {
                    setSkillsRequired([...skillsRequired, v]);
                  }
                  setSkillDraft('');
                }
              }}
            />
          </div>

          <div className={styles.modePane}>
            <label className={styles.fieldLabel}>
              {t('unifiedTeamTab.editRole.timeLabel', { defaultValue: 'Time commitment' })}
            </label>
            <input
              type="text"
              className={styles.textInput}
              value={timeCommitment}
              onChange={(e) => setTimeCommitment(e.target.value)}
              placeholder={t('unifiedTeamTab.editRole.timePlaceholder', { defaultValue: 'e.g. 4 hrs/week' }) as string}
              disabled={busy}
            />
          </div>

          {/* Permission bundle now lives inside the Role definition card
              above — coupled because the catalog link drives the
              permission cascade. */}
          {/* ── End DEV-1159 Phase A ─────────────────────────── */}
          </>)}

          {/* ── RHYTHM tab ─────────────────────────────────────── */}
          {activeTab === 'rhythm' && (<>
          {!sourceDefinitionGuid && (
            <div className={styles.modePane}>
              <div className={styles.clearNotice}>
                {t('unifiedTeamTab.editRole.rhythmRequiresCatalog', {
                  defaultValue: 'This slot is not linked to a catalog role. Link a role definition on the Role tab to see its rhythm.',
                })}
              </div>
            </div>
          )}
          {/* ── DEV-1159 Sub-phase B1: read-only rhythm display ── */}
          {sourceDefinitionGuid && (
            <div className={styles.modePane}>
              <label className={styles.fieldLabel}>
                {t('unifiedTeamTab.editRole.rhythmLabel', { defaultValue: 'Rhythm (recurring duties)' })}
              </label>

              {rhythmLoading && (
                <div className={styles.helpText}>
                  {t('unifiedTeamTab.editRole.rhythmLoading', { defaultValue: 'Loading rhythm…' })}
                </div>
              )}

              {rhythmError && (
                <div className={styles.errorRow}>
                  {t('unifiedTeamTab.editRole.rhythmError', { defaultValue: 'Could not load rhythm' })}: {rhythmError}
                </div>
              )}

              {!rhythmLoading && !rhythmError && !rhythmDetails && (
                <div className={styles.helpText}>
                  {t('unifiedTeamTab.editRole.rhythmNone', {
                    defaultValue: 'This catalog role has no rhythm attached. Editing rhythms lands in a follow-up phase.',
                  })}
                </div>
              )}

              {!rhythmLoading && !rhythmError && rhythmDetails && (
                <>
                  <div className={editStyles.rhythmHeader}>
                    <span className={editStyles.rhythmName}>{rhythmDetails.name}</span>
                    {rhythmDetails.isSystemLocked && (
                      <span className={editStyles.rhythmLockedBadge}>
                        {t('unifiedTeamTab.editRole.rhythmInherited', { defaultValue: 'Inherited from Platform' })}
                      </span>
                    )}
                  </div>
                  {rhythmDetails.description && (
                    <div className={styles.helpText}>{rhythmDetails.description}</div>
                  )}
                  {rhythmDetails.tasks.length === 0 ? (
                    <div className={styles.helpText}>
                      {t('unifiedTeamTab.editRole.rhythmNoTasks', { defaultValue: 'No tasks defined on this rhythm.' })}
                    </div>
                  ) : (
                    <ul className={editStyles.rhythmTaskList}>
                      {rhythmDetails.tasks.map((task) => (
                        <li key={task.taskGuid} className={editStyles.rhythmTask}>
                          <div className={editStyles.rhythmTaskHead}>
                            <span className={editStyles.rhythmTaskName}>{task.name}</span>
                            <span className={editStyles.rhythmTaskMeta}>
                              {task.taskKind === 'standing_duty'
                                ? t('unifiedTeamTab.editRole.rhythmStandingDuty', { defaultValue: 'standing duty' })
                                : task.cadenceType || 'recurring'}
                            </span>
                          </div>
                          {task.description && (
                            <div className={editStyles.rhythmTaskDesc}>{task.description}</div>
                          )}
                          {task.steps.length > 0 && (
                            <ol className={editStyles.rhythmStepList}>
                              {task.steps.map((step) => (
                                <li key={step.stepGuid} className={editStyles.rhythmStep}>
                                  <span>{step.label}</span>
                                  {step.isRequired && (
                                    <span className={editStyles.rhythmStepRequired}>
                                      {t('unifiedTeamTab.editRole.rhythmStepRequired', { defaultValue: 'required' })}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className={styles.helpText} style={{ marginTop: 8, fontStyle: 'italic' }}>
                    {t('unifiedTeamTab.editRole.rhythmReadOnlyNote', {
                      defaultValue: 'Read-only in this phase. Inline editing lands next.',
                    })}
                  </div>
                </>
              )}
            </div>
          )}
          {/* ── End DEV-1159 Sub-phase B1 ────────────────────── */}

          {/* Admin notes — about THIS role (responsibilities, handoff
              context, why this slot exists). Belongs on Role tab, not
              Person tab. Admin-internal: never surfaced publicly. */}
          <div className={styles.modePane}>
            <label className={styles.fieldLabel}>
              {t('unifiedTeamTab.editRole.notesLabel', {
                defaultValue: 'Notes (admin-internal)',
              })}
            </label>
            <textarea
              className={editStyles.textArea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('unifiedTeamTab.editRole.notesPlaceholder', {
                defaultValue: 'Context only admins will see…',
              }) as string}
              disabled={busy}
              rows={3}
            />
          </div>
          </>)}

          {/* Persistent admin-metadata strip retired 2026-06-01 — its
              visual placement made it read as "Role tab content" rather
              than always-visible chrome. Hide flag moved into Person
              tab (about who's publicly visible); Notes moved into Role
              tab (admin notes about the role itself). */}

          {error && <div className={styles.errorRow}>{error}</div>}
        </Tray.Body>

        <Tray.Footer className={styles.footer}>
          {/* Remove (destructive) — left-aligned per convention. Hidden
              in create mode (nothing to remove). Confirm dialog names
              the cascade. */}
          {!isCreate && (
            <button
              type="button"
              className={editStyles.photoBtnDanger}
              onClick={handleRemove}
              disabled={busy}
              style={{ marginInlineEnd: 'auto' }}
            >
              {/* DEV-1193 follow-up: explicit "Remove Role" so admin
                  can't confuse this with the Person tab's "Revoke"
                  (Revoke ends assignment; Remove destroys the slot). */}
              {t('unifiedTeamTab.editRole.removeRoleBtn', { defaultValue: 'Remove Role' })}
            </button>
          )}
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          {/* DEV-1193 follow-up: when the save will fire an invitation
              (linked-mode + new occupant), the button reads "Send
              invitation" so admin knows what's about to happen. Plain
              "Save" for metadata-only edits / external / vacant modes. */}
          {(() => {
            const currentOccupantUpper = (slot.linkedUserGuid || '').toUpperCase();
            const pickedUserUpper = (linkedUserGuid || '').toUpperCase();
            const willInvite =
              fillMode === 'linked' &&
              !!linkedUserGuid &&
              currentOccupantUpper !== pickedUserUpper;
            return (
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={busy || !displayTitle.trim()}
              >
                {busy
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : willInvite
                    ? t('unifiedTeamTab.editRole.sendInvitationBtn', {
                        defaultValue: 'Send invitation',
                      })
                    : t('common.save', { defaultValue: 'Save' })}
              </button>
            );
          })()}
        </Tray.Footer>
      </Tray>

      {pendingCrop && (
        <CircleCropModal
          imageSrc={pendingCrop.src}
          naturalW={pendingCrop.w}
          naturalH={pendingCrop.h}
          onConfirm={handleAvatarCropConfirm}
          onCancel={handleAvatarCropCancel}
        />
      )}
    </>
  );
};

export default SlotEditModal;
